import '../css/header.css'
import '../css/FormNavbar.css'
import '../css/common.css'
import { useNavigate } from 'react-router-dom'
import { Button } from '@enterprise-ui/canvas-ui-react'
import { useState, useEffect, useCallback, useRef } from 'react'
import { logEvent, LogLevel } from '@praxis/component-logging'
import {
  saveTMAuthContext,
  saveCaptivePortalContext,
  clearCaptivePortalContext,
  clearTMAuthContext,
  buildSuccessUrl,
  loadCaptivePortalContext,
  loadTMAuthContext,
} from '../utils/tmAuthStorage'
import { TEAM_MEMBER_RECONNECT_PATH } from '../utils/apiRoutes'

// H1: both base_grant_url and user_continue_url share the same allowed-domain list
const ALLOWED_DOMAINS = ['target.com', 'network-auth.com']

function isSafeBaseGrantUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === 'https:' &&
      ALLOWED_DOMAINS.some(
        (domain) =>
          parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`),
      )
    )
  } catch (e) {
    console.error('Invalid base_grant_url:', e)
    return false
  }
}

// H1: apply the same domain whitelist to user_continue_url (was accepting any http/https URL)
function isValidUserContinueUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      ALLOWED_DOMAINS.some(
        (domain) =>
          parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`),
      )
    )
  } catch (e) {
    console.error('Invalid user_continue_url:', e)
    return false
  }
}

function generateFlowId() {
  return (
    window.crypto?.randomUUID?.() ||
    `tm-${Date.now()}-${Math.random().toString(16).slice(2)}`
  )
}

function normalizeMacAddress(macAddress) {
  return macAddress?.trim().toLowerCase() || null
}

const FormNavbar = ({ config }) => {
  const navigate = useNavigate()
  const hasAutoResumedRef = useRef(false)

  const [mac, setMac] = useState(null)
  const [loc, setLoc] = useState(null)
  const [baseGrantUrl, setBaseGrantUrl] = useState(null)
  const [userContinueUrl, setUserContinueUrl] = useState(null)
  const [successUrl, setSuccessUrl] = useState(null)
  const [showTerms, setShowTerms] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [selectedFlow, setSelectedFlow] = useState(null)
  const [isNavigatingToTM, setIsNavigatingToTM] = useState(false)

  const isProd = config?.environment === 'production'
  const runtimeConfig = config

  useEffect(() => {
    if (!showTerms) {
      setAcceptedTerms(false)
      setErrorMessage(null)
      setSelectedFlow(null)
    }
  }, [showTerms])

  useEffect(() => {
    if (!runtimeConfig) return

    const url = new URL(window.location.href)

    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'

    const queryClientMAC = url.searchParams.get('client_mac')
    const queryBaseUrl = url.searchParams.get('base_grant_url')
    const queryUserContinueUrl = url.searchParams.get('user_continue_url')
    const nodemac = url.searchParams.get('node_mac')

    // N-H3: AP MAC → location mapping loaded from runtime config instead of
    // hardcoded in source (avoids physical network infrastructure disclosure).
    // In config.json / TAP env: "nodeMacMap": { "<mac>": "<location>", ... }
    const nodeMacMap = runtimeConfig?.nodeMacMap || {}
    let resolvedLoc = nodeMacMap[nodemac] || 'local'

    setLoc(resolvedLoc)

    const testMac = runtimeConfig?.testMac
    let resolvedMac = queryClientMAC

    if (!resolvedMac) {
      if (isProd || !testMac) {
        logEvent(
          { message: 'MAC address missing — blocking redirect in production' },
          { level: LogLevel.Error },
        )
      } else {
        resolvedMac = testMac
      }
    }

    const normalizedCurrentMac = normalizeMacAddress(resolvedMac)
    const savedTM = loadTMAuthContext()
    const savedPortal = loadCaptivePortalContext()

    if (
      normalizedCurrentMac &&
      savedTM?.flowId &&
      normalizeMacAddress(savedTM.mac) !== normalizedCurrentMac
    ) {
      logEvent(
        {
          message: `Landing page MAC changed: current=${normalizedCurrentMac} vs savedTM=${savedTM.mac}. Clearing stale TM session.`,
        },
        { level: LogLevel.Warn },
      )
      clearTMAuthContext()
    }

    if (
      normalizedCurrentMac &&
      savedPortal?.mac &&
      normalizeMacAddress(savedPortal.mac) !== normalizedCurrentMac
    ) {
      logEvent(
        {
          message: `Landing page MAC changed: current=${normalizedCurrentMac} vs savedPortal=${savedPortal.mac}. Clearing stale captive portal context.`,
        },
        { level: LogLevel.Warn },
      )
      clearCaptivePortalContext()
    }

    setMac(normalizedCurrentMac)

    const devBaseUrl =
      !isProd && !queryBaseUrl ? runtimeConfig?.devBaseGrantUrl || null : null

    const devUserContinueUrl =
      !isProd && !queryUserContinueUrl
        ? runtimeConfig?.devUserContinueUrl || null
        : null

    const resolvedBaseUrl = queryBaseUrl || devBaseUrl
    const resolvedUserContinueUrl = queryUserContinueUrl || devUserContinueUrl

    if (!resolvedBaseUrl || !resolvedUserContinueUrl) {
      setBaseGrantUrl(null)
      setUserContinueUrl(null)
      setSuccessUrl(null)
      clearCaptivePortalContext()

      logEvent(
        {
          message: `Missing captive portal params. base_grant_url: ${resolvedBaseUrl}, user_continue_url: ${resolvedUserContinueUrl}`,
        },
        { level: LogLevel.Warn },
      )
      return
    }

    if (!isProd && isLocalhost) {
      logEvent(
        {
          message: 'Using localhost dev fallback captive portal URLs',
        },
        { level: LogLevel.Info },
      )
    }

    if (!isSafeBaseGrantUrl(resolvedBaseUrl)) {
      setBaseGrantUrl(null)
      setUserContinueUrl(null)
      setSuccessUrl(null)
      clearCaptivePortalContext()
      return
    }

    if (!isValidUserContinueUrl(resolvedUserContinueUrl)) {
      setBaseGrantUrl(null)
      setUserContinueUrl(null)
      setSuccessUrl(null)
      clearCaptivePortalContext()
      return
    }

    const successRedirectUrl = buildSuccessUrl(
      resolvedBaseUrl,
      resolvedUserContinueUrl,
    )

    if (!successRedirectUrl) {
      setBaseGrantUrl(null)
      setUserContinueUrl(null)
      setSuccessUrl(null)
      clearCaptivePortalContext()
      return
    }

    setBaseGrantUrl(resolvedBaseUrl)
    setUserContinueUrl(resolvedUserContinueUrl)
    setSuccessUrl(successRedirectUrl)

    saveCaptivePortalContext({
      mac: normalizedCurrentMac,
      baseGrantUrl: resolvedBaseUrl,
      userContinueUrl: resolvedUserContinueUrl,
      successUrl: successRedirectUrl,
      loc: resolvedLoc,
      deviceType: window.navigator.userAgent,
    })
  }, [runtimeConfig, isProd])

  useEffect(() => {
    if (!runtimeConfig || hasAutoResumedRef.current) return

    const url = new URL(window.location.href)
    const currentUrlMac = normalizeMacAddress(
      url.searchParams.get('client_mac'),
    )
    const fallbackMac = !isProd
      ? normalizeMacAddress(runtimeConfig?.testMac)
      : null
    const currentMac = currentUrlMac || fallbackMac

    const savedTM = loadTMAuthContext()
    if (!savedTM?.flowId) return
    if (savedTM.step === 'approved') return

    if (!currentMac) {
      logEvent(
        {
          message:
            'No live MAC available for TM auto-resume. Clearing stale TM session.',
        },
        { level: LogLevel.Warn },
      )
      clearTMAuthContext()
      return
    }

    if (currentMac !== normalizeMacAddress(savedTM.mac)) {
      logEvent(
        {
          message: `MAC mismatch detected: current=${currentMac} vs saved=${savedTM.mac}. Clearing stale TM session.`,
        },
        { level: LogLevel.Warn },
      )
      clearTMAuthContext()
      return
    }

    hasAutoResumedRef.current = true
    setSelectedFlow('tm')

    const search = new URLSearchParams({
      flow_id: savedTM.flowId,
      base_grant_url: savedTM.baseGrantUrl || '',
      user_continue_url: savedTM.userContinueUrl || '',
      mac_address: currentMac,
      location: savedTM.loc || 'local',
    }).toString()

    navigate(`/tm?${search}`, {
      state: {
        ...savedTM,
        mac: currentMac,
      },
      replace: true,
    })
  }, [runtimeConfig, navigate, isProd])

  const reconnectTM = useCallback(
    async (macaddr, successurl, location, signal) => {
      if (!runtimeConfig) {
        logEvent(
          { message: 'Reconnect config missing' },
          { level: LogLevel.Error },
        )
        return
      }

      const res = await fetch(TEAM_MEMBER_RECONNECT_PATH, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          mac_address: macaddr,
          location,
          device_type: window.navigator.userAgent,
        }),
      })

      if (!res.ok) {
        throw new Error(`Reconnect failed with status ${res.status}`)
      }

      const response = await res.json()

      if (!signal?.aborted && response?.message === true) {
        window.location.replace(successurl)
      }
    },
    [runtimeConfig],
  )

  useEffect(() => {
    if (!mac || !loc || !successUrl || isNavigatingToTM) return

    const controller = new AbortController()

    reconnectTM(mac, successUrl, loc, controller.signal).catch((err) => {
      if (err?.name === 'AbortError') return

      logEvent(
        { message: 'Reconnect failed', error: err },
        { level: LogLevel.Error },
      )
    })

    return () => controller.abort()
  }, [mac, loc, successUrl, reconnectTM, isNavigatingToTM])

  const handleTeamMemberClick = () => {
    setSelectedFlow('tm')
    setShowTerms(true)
  }

  const handleGuestClick = () => {
    setSelectedFlow('guest')
    setShowTerms(true)
  }

  const handleTermsSubmit = () => {
    if (!acceptedTerms) {
      setErrorMessage('Please accept the terms to continue.')
      return
    }

    if (!mac) {
      setErrorMessage(
        'Unable to detect device MAC address from the captive portal. Please reconnect to Wi-Fi and try again.',
      )
      return
    }

    if (!baseGrantUrl || !userContinueUrl || !successUrl) {
      setErrorMessage(
        'Unable to read captive portal redirect details. Please reconnect to Wi-Fi and try again.',
      )
      return
    }

    const commonContext = {
      mac,
      baseGrantUrl,
      userContinueUrl,
      loc,
      deviceType: window.navigator.userAgent,
      successUrl,
    }

    saveCaptivePortalContext(commonContext)

    logEvent(
      { message: `Terms accepted for ${selectedFlow}` },
      { level: LogLevel.Info },
    )

    const chosenFlow = selectedFlow
    setShowTerms(false)

    if (chosenFlow === 'guest') {
      navigate('/guest', {
        state: commonContext,
      })
      return
    }

    if (chosenFlow === 'tm') {
      setIsNavigatingToTM(true)
      clearTMAuthContext()

      const tmContext = {
        flowId: generateFlowId(),
        ...commonContext,
        step: 'login_started',
      }

      saveTMAuthContext(tmContext)
      saveCaptivePortalContext(tmContext)

      const tmSearch = new URLSearchParams({
        flow_id: tmContext.flowId,
        base_grant_url: baseGrantUrl,
        user_continue_url: userContinueUrl,
        mac_address: mac,
        location: loc || 'local',
      }).toString()

      navigate(`/tm?${tmSearch}`, {
        state: tmContext,
      })
    }
  }

  return (
    <div className="form-navbar-container">
      <div className="form-navbar__header">
        <div className="form-navbar__logo">
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="45" fill="#cc0000" />
            <circle cx="50" cy="50" r="28" fill="white" />
            <circle cx="50" cy="50" r="14" fill="#cc0000" />
          </svg>
        </div>
      </div>

      <h1 className="form-navbar__title">Connect to Guest Wi-Fi</h1>
      <div className="form-navbar__subtitle">Select your access type</div>

      <div className="form-navbar__cards">
        <div
          className={`form-navbar__card ${selectedFlow === 'tm' ? 'selected' : ''}`}
          onClick={handleTeamMemberClick}
        >
          <div className="form-navbar__card-title">
            Team Member / Contractor
          </div>
          <div className="form-navbar__card-desc">Use your Target login</div>
        </div>

        <div
          className={`form-navbar__card ${selectedFlow === 'guest' ? 'selected' : ''}`}
          onClick={handleGuestClick}
        >
          <div className="form-navbar__card-title">Guest</div>
          <div className="form-navbar__card-desc">Limited access</div>
        </div>
      </div>

      <div className="form-navbar__spacer"></div>

      {showTerms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="terms-modal-header">
              Target India Wireless Terms of Use
            </h2>
            <div className="terms-modal-content">
              <p>
                BY CONNECTING TO THIS WIRELESS NETWORK (the "Service"), YOU
                ACKNOWLEDGE THAT YOU HAVE READ ALL OF THE TERMS AND CONDITIONS
                OF THIS WIRELESS TERMS OF USE ("TERMS OF USE") BETWEEN YOU AND
                TARGET CORPORATION INDIA ("Target"), UNDERSTAND THEM, AGREE TO
                BE LEGALLY BOUND BY THEM AND HAVE THE LEGAL CAPACITY TO DO SO.
                If you do not agree to these Terms of Use, do not use or access
                the Service.
              </p>

              <p>
                The Service is offered by Target Corporation India and is not
                intended to subject Target to the laws or jurisdiction of any
                state, country or territory other than that of the India. You
                are responsible for complying with all local laws, rules and
                regulations. Target reserves the right to charge fees in
                connection with the Service or to suspend or discontinue the
                Service, in whole or in part, at any time and in Target’s sole
                discretion.
              </p>

              <p>
                <b>Your Responsibilities</b>
              </p>

              <p>
                Use of the Service is at your own risk. There are privacy and
                security risks associated with connecting to the Internet over
                wireless communications. The Service is not encrypted or secured
                in any way, and any material or other communications that you
                transmit or receive using the Service may be accessible to third
                parties. While using the Service, you assume full responsibility
                and risk for: (i) your own privacy and security; (ii)
                implementing any safeguards you deem to be appropriate to
                protect and secure your privacy and systems; and (iii)
                evaluating the suitability, appropriateness or legality of any
                informational content or other materials you may encounter
                online
              </p>

              <p>
                <b>Confidentiality</b>
              </p>

              <p>
                This service is via an open network and hence any one including
                third parties may access your data or files or otherwise monitor
                your connection. Target may access, preserve or disclose any
                such information for purposes of ensuring your compliance with
                these Terms of Use and applicable law, cooperating with legal
                authorities, and otherwise protecting the rights, property and
                interests of Target and its guests and team members. Target
                cannot assure that your communications or activities will be
                private or secure during your use of the Service. YOU DO NOT
                HAVE AN EXPECTATION OF PRIVACY OR CONFIDENTIALITY WHEN USING THE
                SERVICE
              </p>

              <p>
                <b>Privacy</b>
              </p>

              <p>
                Target will collect, use, share and protect information about
                you, except when complying to legal / regulatory enquires, in
                connection with your use of the Service.
              </p>

              <p>
                We automatically collect information about your use of the
                Service, including:
              </p>

              <ul style={{ paddingLeft: 20 }}>
                <li>Your name and mobile number</li>
                <li>
                  Session data, e.g. date and time device connected to the
                  Service, length of session
                </li>
                <li>Device data</li>
              </ul>

              <p>Ways we use the information we collect include:</p>

              <ul style={{ paddingLeft: 20 }}>
                <li>
                  Internal operations, e.g. enhancing the effectiveness of the
                  Service, analyzing how the Service is used and improving our
                  mobile experience
                </li>
                <li>
                  Legal compliance, e.g. assist law enforcement and respond to
                  legal/regulatory inquiries
                </li>
              </ul>

              <p>
                Information may be shared with the following, during
                transmission & in connection with accessing and the use of this
                service and they may use and disclose any such information for
                the purposes of i) administering the service, ii) providing
                service functionality iii) analysis iv) ensure compliance with
                this agreement v) adhering to applicable laws:
              </p>

              <ul style={{ paddingLeft: 20 }}>
                <li>
                  Within Target Corporation, including Target subsidiaries and
                  affiliates.
                </li>
                <li>
                  With service providers- companies providing support service
                  (e.g., technical support) that may need information to perform
                  their functions.
                </li>
                <li>
                  With third parties (generally this information is anonymous
                  and/or aggregated)
                </li>
                <li>
                  For legal requirements- disclosure may occur to comply with
                  law, to enforce or ensure compliance with the Terms of Use, or
                  to protect the rights, property or safety of our company, our
                  team or others.
                </li>
              </ul>

              <p>How we protect information:</p>

              <p>
                We maintain administrative, technical and physical safeguards to
                protect your information
              </p>

              <p>Information about Children</p>

              <p>
                If you are under 18, you may use the Service only with
                involvement of a parent or guardian, who is bound by these Terms
                of Use and such parent or guardian will be fully responsible for
                such child’s use of the Service including financial charges and
                legal liability that such child may incur.
              </p>

              <p>
                <b>Prohibited use</b>
              </p>

              <p>
                You agree not to use the Service to transmit or receive any
                material or other communications that: (i) are unlawful,
                malicious, fraudulent, harassing, obscene, indecent, tortious or
                harmful to Target, its guests or any other party; (ii) violate
                another’s intellectual property, privacy or other rights
                (including by intercepting, collecting or harvesting any
                material or other communications transmitted or received by
                other users of the Service); or (iii) otherwise interfere with
                the operation, use or enjoyment of the Service or any other
                service, system or property. In addition, you may not use the
                Service to transmit or receive any virus, worm, Trojan horse,
                Easter egg, time bomb, spyware or other computer code, file or
                program that is harmful or invasive or may or is intended to
                damage or hijack the operation of, or to monitor the use of, any
                hardware, software or equipment. Target has the right (but not
                the obligation), at any time and at its sole discretion, to
                block inappropriate material, websites or other resources,
                and/or suspend or terminate an individual’s access to the
                Service for engaging in these prohibited uses, or for any other
                reason or no reason at all.
              </p>

              <p>
                <b>Disclaimers</b>
              </p>

              <p>
                THE SERVICE IS PROVIDED SOLELY AS A CONVENIENCE TO YOU, "AS IS"
                AND "AS AVAILABLE". TARGET AND ITS AFFILIATES, SHAREHOLDERS,
                DIRECTORS, OFFICERS, TEAM MEMBERS AND AGENTS DISCLAIM ALL
                WARRANTIES AND REPRESENTATIONS OF ANY KIND, WHETHER EXPRESS,
                IMPLIED OR STATUTORY, INCLUDING WITHOUT LIMITATION ANY
                WARRANTIES: (i) OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                PURPOSE, OR NON-INFRINGEMENT; OR (ii) THAT ACCESS TO OR USE OF
                THIS SERVICE OR ANY PART THEREOF WILL BE UNINTERRUPTED, FREE OF
                VIRUSES OR OTHER HARMFUL CODE, OR SECURE, OR THAT PROBLEMS WILL
                BE CORRECTED, EVEN IF TARGET IS ON NOTICE OF SUCH PROBLEMS. YOU
                AGREE THAT NO ADVICE OR INFORMATION GIVEN BY TARGET OR ITS
                EMPLOYEES SHALL CREATE ANY WARRANTY. YOU AGREE THAT YOUR USE OF
                THIRD-PARTY WEBSITES AND RESOURCES, ACCESSED THROUGH USE OF THE
                SERVICE IS AT YOUR OWN RISK AND IS SUBJECT TO THE TERMS AND
                CONDITIONS OF USE APPLICABLE TO THOSE THIRD-PARTY SITES AND
                RESOURCES
              </p>

              <p>
                <b>Exclusion of liability</b>
              </p>

              <p>
                TARGET AND ITS AFFILIATES, SHAREHOLDERS, DIRECTORS, OFFICERS,
                EMPLOYEES AND AGENTS SHALL HAVE NO LIABILITY FOR ANY DIRECT,
                INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL, PUNITIVE OR
                EXEMPLARY DAMAGES (INCLUDING, WITHOUT LIMITATION, LOST PROFITS,
                LOSS OF BUSINESS, BUSINESS INTERRUPTION, LOSS OF DATA OR
                CONFIDENTIALITY OR SECURITY, OR OTHER SIMILAR DAMAGES) ARISING
                OUT OF OR RELATED TO THIS AGREEMENT OR THE SERVICE, EVEN IF
                TARGET IS ADVISED OF THE POSSIBILITY OF ANY SUCH DAMAGES.
              </p>

              <p>
                <b>Miscellaneous</b>
              </p>

              <p>
                Target reserves the right to make changes to these Terms of Use
                at any time, and such changes will be effective immediately upon
                being posted. Each time you use the Service, you should review
                the current Terms of Use. To tell when the most recent updates
                were made, see the date at the bottom of the Terms of Use. Your
                continued use of the Service will indicate your acceptance of
                the current Terms of Use; but any material change to these Terms
                of Use will not be applied retroactively to any claim or dispute
                between you and us in connection with these Terms of Use that
                arose prior to the applicable material change. If any provision
                of these Terms of Use is found to be unlawful, void or for any
                reason unenforceable, that provision will be deemed severable
                from these Terms of Use and will not affect the validity and
                enforceability of any remaining provision. No waiver by either
                party of any breach or default hereunder will be deemed to be a
                waiver of any preceding or subsequent breach or default. These
                Terms of Use (i) will be governed by and construed in accordance
                with the laws of India; and (ii) constitute the entire agreement
                between you and Target regarding the accepted use and terms of
                the Service.
              </p>

              <p>
                <b>Contact Us:</b>
              </p>

              <p>
                Mail: Target Corporation India Pvt. Ltd.
                <br />
                C2, Manyata Embassy Business Park (SEZ)
                <br />
                Nagawara Village, Outer Ring Road, Hebbal
                <br />
                Bangalore 560045, India
                <br />
                Phone: +91 80467 34000
              </p>

              <p>
                <strong>last update: 08/02/2021</strong>
              </p>
            </div>
            <div className="terms-modal-footer">
              <div className="terms-checkbox-container">
                <label className="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  I agree to the terms and conditions
                </label>
              </div>

              {errorMessage && (
                <div className="terms-error">{errorMessage}</div>
              )}

              <div className="terms-modal-actions">
                <Button
                  onClick={() => setShowTerms(false)}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!acceptedTerms}
                  onClick={handleTermsSubmit}
                  className="btn-primary"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FormNavbar
